/**
 * CenterDetailPage - Dettaglio e modifica Centro
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: Pagina dettaglio centro con:
 * - Visualizzazione info centro
 * - Modifica dati
 * - Statistiche utilizzo
 * - Gestione coordinatori (con PIN)
 * 
 * FASE 6B: Trasferimento centro con Transfer Code
 * - Verifica codice ricevuto da altra Organization
 * - Invio richiesta trasferimento
 * - Reminder GDPR (responsabilità contrattuale su Responsabile)
 * - Stato migrazione in corso (consensi GDPR, progress, deadline)
 * 
 * FIX: Corretta estrazione dati API (res.data.center invece di res.data.data?.center)
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationApi } from '../../services/organizationApi'
import { Card, PageLoader, Badge } from '../../components/ui'

import {
  Building2,
  Users,
  Baby,
  ArrowLeft,
  Save,
  Edit2,
  Loader2,
  MapPin,
  Mail,
  Phone,
  Calendar,
  UserPlus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Settings,
  RefreshCw,
  AlertCircle,
  BarChart3,
  Shield,
  Key,
  Copy,
  X,
  Send,
  ArrowRightLeft,
  FileText,
  UserMinus,
  Crown,
  Eye,
  EyeOff,
  CreditCard,
  ExternalLink,
  TrendingUp,
  Download,
  RotateCcw
} from 'lucide-react'

// ============================================
// STATUS BADGE
// ============================================

function StatusBadge({ status }) {
  const config = {
    trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    active: { label: 'Attivo', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    suspended: { label: 'Sospeso', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    pending_deletion: { label: 'In Cancellazione', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    export_only: { label: 'Solo Esportazione', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    cancelled: { label: 'Cancellato (legacy)', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
    deleted: { label: 'Eliminato', color: 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-500' }
  }

  const { label, color } = config[status] || {
    label: 'Sconosciuto',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }
  
  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${color}`}>
      {label}
    </span>
  )
}

// ============================================
// MIGRATION STATUS PANEL
// ============================================

function MigrationStatusPanel({ migration, onRefresh }) {
  if (!migration) return null
  
  const isOrgTransfer = migration.type === 'organization_transfer'
  const startDate = migration.startedAt ? new Date(migration.startedAt).toLocaleDateString('it-IT') : null
  const deadlineDate = migration.consentDeadline ? new Date(migration.consentDeadline).toLocaleDateString('it-IT') : null
  
  // Calcola giorni rimanenti alla deadline
  const daysToDeadline = migration.consentDeadline 
    ? Math.max(0, Math.ceil((new Date(migration.consentDeadline) - new Date()) / (1000 * 60 * 60 * 24)))
    : null
  
  const consents = migration.consents || {}
  const totalConsents = (consents.total || 0)
  const grantedConsents = (consents.granted || 0)
  const deniedConsents = (consents.denied || 0)
  const pendingConsents = (consents.pending || 0)
  const progressPercent = totalConsents > 0 ? Math.round(((grantedConsents + deniedConsents) / totalConsents) * 100) : 0
  
  return (
    <div className="space-y-4">
      {/* Banner stato */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex-shrink-0">
            <ArrowRightLeft className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-purple-900 dark:text-purple-100">
                Migrazione in Corso
              </h4>
              <span className="px-2 py-0.5 bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 text-xs font-medium rounded-full">
                {isOrgTransfer ? 'Trasferimento Organization' : 'Cambio Coordinatore'}
              </span>
            </div>
            
            {/* Destinazione */}
            {migration.targetOrganization && (
              <p className="text-sm text-purple-800 dark:text-purple-300 mt-1">
                Destinazione: <strong>{migration.targetOrganization.name}</strong>
              </p>
            )}
            
            {/* Date */}
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-purple-700 dark:text-purple-300">
              {startDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Avviata il {startDate}
                </span>
              )}
              {deadlineDate && (
                <span className={`flex items-center gap-1 ${daysToDeadline !== null && daysToDeadline <= 5 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                  <Clock className="h-3.5 w-3.5" />
                  Scadenza consensi: {deadlineDate}
                  {daysToDeadline !== null && ` (${daysToDeadline} giorni)`}
                </span>
              )}
            </div>
            
            {migration.notes && (
              <p className="text-sm text-purple-700 dark:text-purple-300 mt-2 italic">
                Note: "{migration.notes}"
              </p>
            )}
          </div>
          
          <button
            onClick={onRefresh}
            className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-800/50 rounded-lg transition-colors flex-shrink-0"
            title="Aggiorna stato"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Consensi GDPR (solo se richiesti) */}
      {migration.consentRequired && totalConsents > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-600" />
              Consensi GDPR
            </h5>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {grantedConsents + deniedConsents} / {totalConsents} raccolti
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <div className="flex h-2.5 rounded-full overflow-hidden">
              {grantedConsents > 0 && (
                <div 
                  className="bg-green-500 h-full" 
                  style={{ width: `${(grantedConsents / totalConsents) * 100}%` }}
                />
              )}
              {deniedConsents > 0 && (
                <div 
                  className="bg-red-500 h-full" 
                  style={{ width: `${(deniedConsents / totalConsents) * 100}%` }}
                />
              )}
            </div>
          </div>
          
          {/* Dettagli consensi */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-lg font-bold text-green-700 dark:text-green-400">{grantedConsents}</p>
              <p className="text-xs text-green-600 dark:text-green-400">Concessi</p>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-lg font-bold text-red-700 dark:text-red-400">{deniedConsents}</p>
              <p className="text-xs text-red-600 dark:text-red-400">Negati</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{pendingConsents}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">In attesa</p>
            </div>
          </div>
          
          {/* Reminder responsabilità */}
          <div className="flex items-start gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              La raccolta dei consensi è a carico tuo in qualità di Titolare del trattamento. 
              Contatta i genitori/tutori dei minori e registra le risposte.
            </p>
          </div>
        </div>
      )}
      
      {/* Stato completamento */}
      <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
        migration.canComplete 
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200'
      }`}>
        {migration.canComplete ? (
          <>
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <span>La migrazione può essere completata. L'organizzazione ricevente deve approvare il trasferimento.</span>
          </>
        ) : (
          <>
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span>In attesa del completamento della raccolta consensi prima di procedere al trasferimento.</span>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================
// STAT CARD
// ============================================

function StatCard({ icon: Icon, label, value, color = 'primary' }) {
  const colors = {
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

// ============================================
// BILLING FIELD - Cascata visiva
// ============================================

function BillingField({ label, centerValue, orgValue }) {
  const hasCustomValue = centerValue && centerValue.trim() !== ''
  const hasOrgValue = orgValue && orgValue.trim() !== ''
  const displayValue = hasCustomValue ? centerValue : (hasOrgValue ? orgValue : null)
  
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      {displayValue ? (
        <div>
          <p className={`${hasCustomValue ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 italic'}`}>
            {displayValue}
          </p>
          <span className={`text-xs mt-0.5 inline-block ${
            hasCustomValue 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-gray-400 dark:text-gray-500'
          }`}>
            {hasCustomValue ? 'Personalizzato per questo centro' : 'Ereditato dall\'organizzazione'}
          </span>
        </div>
      ) : (
        <p className="text-gray-400 dark:text-gray-500 italic">Non configurato</p>
      )}
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function CenterDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  
  // === Stati base ===
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [center, setCenter] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    entityType: 'cooperativa',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: ''
    },
    billing: {
      businessName: '',
      vatNumber: '',
      fiscalCode: '',
      pecEmail: '',
      sdiCode: ''
    },
    website: ''
  })
  
  // === Stati Coordinatori ===
  const [coordinators, setCoordinators] = useState([])
  const [loadingCoordinators, setLoadingCoordinators] = useState(false)
  const [mainCoordinatorId, setMainCoordinatorId] = useState(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteForm, setInviteForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    setAsMain: false,
    requirePin: true // Default: PIN richiesto (consigliato)
  })
  const [inviting, setInviting] = useState(false)
  const [inviteResult, setInviteResult] = useState(null) // { pin, nome, cognome, email }
  const [pinCopied, setPinCopied] = useState(false)
  const [removingCoordinator, setRemovingCoordinator] = useState(null)
  
  // === Stati Modal Dettaglio Coordinatore ===
  const [selectedCoordinator, setSelectedCoordinator] = useState(null)
  const [showCoordinatorModal, setShowCoordinatorModal] = useState(false)
  const [loadingCoordinatorInfo, setLoadingCoordinatorInfo] = useState(false)
  const [coordinatorInviteInfo, setCoordinatorInviteInfo] = useState(null)
  const [resendingInvite, setResendingInvite] = useState(false)
  const [resendResult, setResendResult] = useState(null) // { pin, message }
  
  // === Stati Trasferimento ===
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [transferCode, setTransferCode] = useState('')
  const [verifyingCode, setVerifyingCode] = useState(false)
  const [verifiedOrg, setVerifiedOrg] = useState(null) // Org destinazione verificata
  const [transferNotes, setTransferNotes] = useState('')
  const [gdprConfirmed, setGdprConfirmed] = useState(false)
  const [sendingTransfer, setSendingTransfer] = useState(false)
  const [outgoingRequests, setOutgoingRequests] = useState([])
  const [loadingRequests, setLoadingRequests] = useState(false)
  
  // === Stati Migrazione ===
  const [migrationStatus, setMigrationStatus] = useState(null)
  const [loadingMigration, setLoadingMigration] = useState(false)

  // === Stato Annulla cancellazione ===
  const [cancelingDeletion, setCancelingDeletion] = useState(false)
  
  // ============================================
  // LOAD DATA
  // ============================================
  
  const loadCenter = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [centerRes, orgRes] = await Promise.all([
        organizationApi.getCenter(id),
        organizationApi.getCurrent()
      ])
      
      // FIX: L'API restituisce { success: true, center: {...} }
      const centerData = centerRes.data.center || centerRes.data.data?.center || centerRes.data.data
      const orgData = orgRes.data.organization || orgRes.data.data
      
      if (!centerData) {
        throw new Error('Dati centro non trovati nella risposta')
      }
      
      setCenter(centerData)
      setOrganization(orgData)
      setFormData({
        name: centerData.name || '',
        entityType: centerData.entityType || 'cooperativa',
        email: centerData.contacts?.email || centerData.email || '',
        phone: centerData.contacts?.phone || centerData.phone || '',
        website: centerData.contacts?.website || '',
        address: {
          street: centerData.address?.street || '',
          city: centerData.address?.city || '',
          province: centerData.address?.province || '',
          postalCode: centerData.address?.postalCode || ''
        },
        billing: {
          businessName: centerData.billing?.businessName || '',
          vatNumber: centerData.billing?.vatNumber || '',
          fiscalCode: centerData.billing?.fiscalCode || '',
          pecEmail: centerData.billing?.pecEmail || '',
          sdiCode: centerData.billing?.sdiCode || ''
        }
      })
    } catch (err) {
      console.error('Error loading center:', err)
      setError(err.response?.data?.message || err.message || 'Errore nel caricamento del centro')
    } finally {
      setLoading(false)
    }
  }, [id])
  
  const loadCoordinators = useCallback(async () => {
    try {
      setLoadingCoordinators(true)
      const res = await organizationApi.getCenterCoordinators(id)
      const data = res.data
      setCoordinators(data.coordinators || [])
      setMainCoordinatorId(data.mainCoordinator || null)
    } catch (err) {
      console.error('Error loading coordinators:', err)
    } finally {
      setLoadingCoordinators(false)
    }
  }, [id])
  
  const loadOutgoingRequests = useCallback(async () => {
    try {
      setLoadingRequests(true)
      const res = await organizationApi.getOutgoingTransferRequests()
      // Filtra solo le richieste per questo centro
      const requests = (res.data.requests || []).filter(r => 
        r.centerId === id || r.centerSnapshot?.id === id
      )
      setOutgoingRequests(requests)
    } catch (err) {
      console.error('Error loading transfer requests:', err)
    } finally {
      setLoadingRequests(false)
    }
  }, [id])
  
  const loadMigrationStatus = useCallback(async () => {
    try {
      setLoadingMigration(true)
      const res = await organizationApi.getMigrationStatus(id)
      if (res.data.inProgress) {
        setMigrationStatus(res.data.migration)
      } else {
        setMigrationStatus(null)
      }
    } catch (err) {
      console.error('Error loading migration status:', err)
      setMigrationStatus(null)
    } finally {
      setLoadingMigration(false)
    }
  }, [id])
  
  useEffect(() => {
    if (id) {
      loadCenter()
      loadCoordinators()
      loadOutgoingRequests()
      loadMigrationStatus()
    }
  }, [id, loadCenter, loadCoordinators, loadOutgoingRequests, loadMigrationStatus])
  
  // ============================================
  // HANDLERS - Centro
  // ============================================
  
  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    
    try {
      await organizationApi.updateCenter(id, {
        name: formData.name,
        entityType: formData.entityType,
        contacts: {
          email: formData.email,
          phone: formData.phone,
          website: formData.website
        },
        address: formData.address,
        billing: formData.billing
      })
      showSuccess('Centro aggiornato con successo!')
      setIsEditing(false)
      loadCenter()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }
  
  // ============================================
  // HANDLERS - Coordinatori
  // ============================================
  
  const handleInviteCoordinator = async (e) => {
    e.preventDefault()
    setInviting(true)
    
    try {
      const res = await organizationApi.inviteCoordinator(id, inviteForm)
      
      // Se c'è un PIN, mostralo
      if (res.data.pin) {
        setInviteResult({
          pin: res.data.pin,
          nome: inviteForm.nome,
          cognome: inviteForm.cognome,
          email: inviteForm.email,
          message: res.data.pinMessage
        })
        showSuccess('Invito inviato! Comunica il PIN al coordinatore.')
      } else {
        showSuccess('Invito inviato!')
        setShowInviteModal(false)
        resetInviteForm()
      }
      
      loadCoordinators()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'invio dell\'invito')
    } finally {
      setInviting(false)
    }
  }
  
  const handleCopyPin = () => {
    if (inviteResult?.pin) {
      navigator.clipboard.writeText(inviteResult.pin)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    }
  }
  
  const handleCloseInviteModal = () => {
    setShowInviteModal(false)
    setInviteResult(null)
    resetInviteForm()
  }
  
  const resetInviteForm = () => {
    setInviteForm({
      nome: '',
      cognome: '',
      email: '',
      setAsMain: false,
      requirePin: true
    })
  }
  
  const handleRemoveCoordinator = async (coordinatorId) => {
    if (!confirm('Sei sicuro di voler rimuovere questo coordinatore?')) return
    
    setRemovingCoordinator(coordinatorId)
    try {
      await organizationApi.removeCoordinator(id, coordinatorId)
      showSuccess('Coordinatore rimosso')
      loadCoordinators()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella rimozione')
    } finally {
      setRemovingCoordinator(null)
    }
  }
  
  // ============================================
  // HANDLERS - Dettaglio Coordinatore (PIN)
  // ============================================
  
  const handleCoordinatorClick = async (coord) => {
    // Solo per coordinatori non attivi (pending/expired)
    if (coord.attivo) return
    
    setSelectedCoordinator(coord)
    setShowCoordinatorModal(true)
    setLoadingCoordinatorInfo(true)
    setCoordinatorInviteInfo(null)
    setResendResult(null)
    
    try {
      const res = await organizationApi.getCoordinatorInviteInfo(id, coord._id)
      setCoordinatorInviteInfo(res.data.coordinator)
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel recupero info invito')
      setShowCoordinatorModal(false)
    } finally {
      setLoadingCoordinatorInfo(false)
    }
  }
  
  const handleResendInvite = async () => {
    if (!selectedCoordinator) return
    
    setResendingInvite(true)
    setResendResult(null)
    
    try {
      const res = await organizationApi.resendCoordinatorInvite(id, selectedCoordinator._id, {
        requirePin: true // Sempre con PIN per sicurezza
      })
      
      if (res.data.pin) {
        setResendResult({
          pin: res.data.pin,
          message: res.data.pinMessage
        })
        showSuccess('Nuovo invito inviato!')
      } else {
        showSuccess('Nuovo invito inviato!')
        handleCloseCoordinatorModal()
      }
      
      loadCoordinators()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel reinvio dell\'invito')
    } finally {
      setResendingInvite(false)
    }
  }
  
  const handleCloseCoordinatorModal = () => {
    setShowCoordinatorModal(false)
    setSelectedCoordinator(null)
    setCoordinatorInviteInfo(null)
    setResendResult(null)
  }
  
  const handleCopyCoordinatorPin = () => {
    const pin = resendResult?.pin || coordinatorInviteInfo?.pin
    if (pin) {
      navigator.clipboard.writeText(pin)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    }
  }
  
  // ============================================
  // HANDLERS - Trasferimento
  // ============================================
  
  const handleVerifyTransferCode = async () => {
    if (!transferCode.trim()) {
      showError('Inserisci il codice di trasferimento')
      return
    }
    
    setVerifyingCode(true)
    setVerifiedOrg(null)
    
    try {
      const res = await organizationApi.verifyTransferCode(transferCode.trim())
      
      if (res.data.valid) {
        setVerifiedOrg(res.data.organization)
        showSuccess('Codice valido!')
      } else {
        showError(res.data.error || 'Codice non valido')
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella verifica del codice')
    } finally {
      setVerifyingCode(false)
    }
  }
  
  const handleSendTransferRequest = async () => {
    if (!verifiedOrg) {
      showError('Verifica prima il codice')
      return
    }
    
    if (!gdprConfirmed) {
      showError('Devi confermare di aver letto le responsabilità GDPR')
      return
    }
    
    setSendingTransfer(true)
    
    try {
      await organizationApi.sendTransferRequest(id, {
        transferCode: transferCode.trim(),
        gdprConfirmed: true,
        notes: transferNotes
      })
      
      showSuccess('Richiesta di trasferimento inviata!')
      setShowTransferModal(false)
      resetTransferForm()
      loadOutgoingRequests()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'invio della richiesta')
    } finally {
      setSendingTransfer(false)
    }
  }
  
  const handleCancelTransferRequest = async (requestId) => {
    if (!confirm('Sei sicuro di voler annullare questa richiesta?')) return

    try {
      await organizationApi.cancelTransferRequest(requestId)
      showSuccess('Richiesta annullata')
      loadOutgoingRequests()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'annullamento')
    }
  }

  const handleCancelCenterDeletion = async () => {
    if (!confirm(
      `Annullare la cancellazione del centro "${center.name}"?\n\nIl centro tornerà attivo e potrai poi riattivare un piano dalla sezione Fatturazione.`
    )) return

    try {
      setCancelingDeletion(true)
      await organizationApi.cancelCenterDeletion(id)
      showSuccess('Cancellazione annullata. Ora puoi riattivare un piano dalla sezione Fatturazione.')
      await loadCenter()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'annullamento della cancellazione')
    } finally {
      setCancelingDeletion(false)
    }
  }
  
  const resetTransferForm = () => {
    setTransferCode('')
    setVerifiedOrg(null)
    setTransferNotes('')
    setGdprConfirmed(false)
  }
  
  // ============================================
  // RENDER - Loading & Error States
  // ============================================
  
  if (loading) {
    return <PageLoader />
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
            Errore nel caricamento
          </h2>
          <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate('/organization/centers')} className="btn-secondary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna ai Centri
            </button>
            <button onClick={loadCenter} className="btn-primary">
              <RefreshCw className="h-4 w-4 mr-2" />
              Riprova
            </button>
          </div>
        </div>
      </div>
    )
  }
  
  if (!center) {
    return (
      <div className="p-6 text-center">
        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Centro non trovato</p>
        <Link to="/organization/centers" className="btn-primary mt-4 inline-flex">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Centri
        </Link>
      </div>
    )
  }
  
  const minoriCount = center.stats?.minori || center.usage?.minori || 0
  const hasPendingTransfer = outgoingRequests.some(r => r.status === 'pending')
  
  // ============================================
  // RENDER - Main
  // ============================================
  
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/organization/centers"
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {center.name}
              </h1>
              <StatusBadge status={center.status} />
            </div>
          </div>
        </div>
        
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn-secondary"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Modifica
          </button>
        )}
      </div>
      
      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Baby}
          label="Minori"
          value={minoriCount}
          color="primary"
        />
        <StatCard
          icon={Users}
          label="Educatori"
          value={center.stats?.educatori || center.usage?.educatori || 0}
          color="blue"
        />
        <StatCard
          icon={Users}
          label="Coordinatori"
          value={coordinators.length}
          color="green"
        />
        <StatCard
          icon={BarChart3}
          label="Piano"
          value={center.currentPlan || 'Trial'}
          color="amber"
        />
      </div>
      
      {/* Form / Info Centro */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Building2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Informazioni Centro
          </h2>
        </div>
        
        {isEditing ? (
          <form onSubmit={handleSave} className="space-y-6">
            {/* Nome e Tipo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Centro *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo Ente
                </label>
                <select
                  value={formData.entityType}
                  onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                  className="input"
                >
                  <option value="cooperativa">Cooperativa</option>
                  <option value="onlus">ONLUS</option>
                  <option value="privato">Privato</option>
                  <option value="pubblico">Pubblico</option>
                </select>
              </div>
            </div>
            
            {/* Contatti */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sito Web
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://"
                  className="input"
                />
              </div>
            </div>
            
            {/* Indirizzo */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Indirizzo
              </h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Via/Indirizzo"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                  className="input"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <input
                      type="text"
                      placeholder="Città"
                      value={formData.address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, city: e.target.value }
                      })}
                      className="input"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Prov."
                      value={formData.address.province}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, province: e.target.value.toUpperCase() }
                      })}
                      maxLength={2}
                      className="input"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="CAP"
                      value={formData.address.postalCode}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, postalCode: e.target.value }
                      })}
                      maxLength={5}
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Dati Fiscali Centro */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Dati Fiscali Centro
              </h3>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  I campi lasciati vuoti erediteranno automaticamente i valori dell'organizzazione. Compila solo i campi diversi.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ragione Sociale
                  </label>
                  <input
                    type="text"
                    value={formData.billing.businessName}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing: { ...formData.billing, businessName: e.target.value }
                    })}
                    placeholder={organization?.legalName || organization?.name || 'Ereditato dall\'organizzazione'}
                    className="input placeholder:text-gray-400 placeholder:italic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Partita IVA
                  </label>
                  <input
                    type="text"
                    value={formData.billing.vatNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing: { ...formData.billing, vatNumber: e.target.value.replace(/\D/g, '').slice(0, 11) }
                    })}
                    placeholder={organization?.fiscal?.partitaIva || 'Ereditato dall\'organizzazione'}
                    maxLength={11}
                    className="input placeholder:text-gray-400 placeholder:italic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Codice Fiscale
                  </label>
                  <input
                    type="text"
                    value={formData.billing.fiscalCode}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing: { ...formData.billing, fiscalCode: e.target.value.toUpperCase().slice(0, 16) }
                    })}
                    placeholder={organization?.fiscal?.codiceFiscale || 'Ereditato dall\'organizzazione'}
                    maxLength={16}
                    className="input uppercase placeholder:text-gray-400 placeholder:italic placeholder:normal-case"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    PEC
                  </label>
                  <input
                    type="email"
                    value={formData.billing.pecEmail}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing: { ...formData.billing, pecEmail: e.target.value }
                    })}
                    placeholder={organization?.fiscal?.pec || 'Ereditato dall\'organizzazione'}
                    className="input placeholder:text-gray-400 placeholder:italic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Codice SDI
                  </label>
                  <input
                    type="text"
                    value={formData.billing.sdiCode}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing: { ...formData.billing, sdiCode: e.target.value.toUpperCase().slice(0, 7) }
                    })}
                    placeholder={organization?.fiscal?.sdi || 'Ereditato dall\'organizzazione'}
                    maxLength={7}
                    className="input uppercase placeholder:text-gray-400 placeholder:italic placeholder:normal-case"
                  />
                </div>
              </div>
            </div>
            
            {/* Actions - Edit */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false)
                  loadCenter()
                }}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salva Modifiche
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Tipo Ente</p>
                <p className="text-gray-900 dark:text-white capitalize">{center.entityType || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Piano Attuale</p>
                <p className="text-gray-900 dark:text-white capitalize">{center.currentPlan || 'Trial'}</p>
              </div>
            </div>
            
            <hr className="border-gray-200 dark:border-gray-700" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                  <p className="text-gray-900 dark:text-white">
                    {center.contacts?.email || center.email || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Telefono</p>
                  <p className="text-gray-900 dark:text-white">
                    {center.contacts?.phone || center.phone || '-'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Sito Web */}
            {center.contacts?.website && (
              <>
                <hr className="border-gray-200 dark:border-gray-700" />
                <div className="flex items-start gap-3">
                  <ExternalLink className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Sito Web</p>
                    <a 
                      href={center.contacts.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {center.contacts.website}
                    </a>
                  </div>
                </div>
              </>
            )}
            
            <hr className="border-gray-200 dark:border-gray-700" />
            
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Indirizzo</p>
                <p className="text-gray-900 dark:text-white">
                  {center.address ? (
                    <>
                      {center.address.street && <span>{center.address.street}<br /></span>}
                      {center.address.city && <span>{center.address.city} </span>}
                      {center.address.province && <span>({center.address.province}) </span>}
                      {center.address.postalCode && <span>- {center.address.postalCode}</span>}
                    </>
                  ) : '-'}
                </p>
              </div>
            </div>
            
            <hr className="border-gray-200 dark:border-gray-700" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Data Creazione</p>
                  <p className="text-gray-900 dark:text-white">
                    {center.createdAt
                      ? new Date(center.createdAt).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : '-'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
      
      {/* === SEZIONE DATI FISCALI CON CASCATA === */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <CreditCard className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Dati Fiscali
          </h2>
        </div>
        
        {/* Banner cascata */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              I dati fiscali non compilati verranno ereditati dall'organizzazione. Compila solo i campi diversi.
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Griglia campi fiscali con cascata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Ragione Sociale */}
            <BillingField
              label="Ragione Sociale"
              centerValue={center.billing?.businessName}
              orgValue={organization?.legalName || organization?.name}
            />
            
            {/* Partita IVA */}
            <BillingField
              label="Partita IVA"
              centerValue={center.billing?.vatNumber}
              orgValue={organization?.fiscal?.partitaIva}
            />
            
            {/* Codice Fiscale */}
            <BillingField
              label="Codice Fiscale"
              centerValue={center.billing?.fiscalCode}
              orgValue={organization?.fiscal?.codiceFiscale}
            />
            
            {/* PEC */}
            <BillingField
              label="PEC"
              centerValue={center.billing?.pecEmail}
              orgValue={organization?.fiscal?.pec}
            />
            
            {/* Codice SDI */}
            <BillingField
              label="Codice SDI"
              centerValue={center.billing?.sdiCode}
              orgValue={organization?.fiscal?.sdi}
            />
          </div>
        </div>
      </Card>
      
      {/* === SEZIONE COORDINATORI === */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Coordinatori
            </h2>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary btn-sm"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invita
          </button>
        </div>
        
        {loadingCoordinators ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
          </div>
        ) : coordinators.length === 0 ? (
          <div className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
            <AlertTriangle className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
            <p className="text-yellow-800 dark:text-yellow-200 font-medium">
              Nessun coordinatore assegnato
            </p>
            <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
              Questo centro non ha coordinatori. Invitane uno per gestire il centro.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {coordinators.map((coord) => (
              <li
                key={coord._id}
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
              >
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 font-medium">
                    {coord.nome?.[0]}{coord.cognome?.[0]}
                  </span>
                </div>
                {/* Area cliccabile per coordinatori in attesa */}
                <div 
                  className={`flex-1 min-w-0 ${!coord.attivo ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 -m-2 p-2 rounded-lg transition-colors' : ''}`}
                  onClick={() => !coord.attivo && handleCoordinatorClick(coord)}
                  title={!coord.attivo ? 'Clicca per vedere PIN e gestire invito' : undefined}
                >
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {coord.nome} {coord.cognome}
                    </p>
                    {mainCoordinatorId === coord._id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                        <Crown className="h-3 w-3" />
                        Principale
                      </span>
                    )}
                    {/* Indicatore PIN per coordinatori in attesa */}
                    {!coord.attivo && coord.invitePinRequired && (
                      <Key className="h-4 w-4 text-gray-400" title="Richiede PIN" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {coord.email}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {coord.attivo ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                      <CheckCircle className="h-3 w-3" />
                      Attivo
                    </span>
                  ) : coord.inviteStatus === 'pending' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                      <Clock className="h-3 w-3" />
                      In attesa
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                      Inattivo
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveCoordinator(coord._id); }}
                    disabled={removingCoordinator === coord._id}
                    className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Rimuovi coordinatore"
                  >
                    {removingCoordinator === coord._id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserMinus className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      
      {/* === SEZIONE ABBONAMENTO === */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Abbonamento
            </h2>
          </div>
          <Link
            to="/organization/billing"
            className="btn-secondary btn-sm flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Gestisci
          </Link>
        </div>
        
        {center.subscription ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Piano</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">
                  {center.subscription.plan}
                </p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Stato</p>
                <p className={`text-lg font-bold capitalize ${
                  center.subscription.status === 'active' 
                    ? 'text-green-600 dark:text-green-400' 
                    : center.subscription.status === 'trialing'
                      ? 'text-blue-600 dark:text-blue-400'
                      : center.subscription.status === 'past_due'
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {center.subscription.status === 'active' ? 'Attivo' :
                   center.subscription.status === 'trialing' ? 'Prova' :
                   center.subscription.status === 'past_due' ? 'In Ritardo' :
                   center.subscription.status}
                </p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Costo</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {center.subscription.finalPrice > 0 
                    ? `€${(center.subscription.finalPrice / 100).toFixed(2)}`
                    : 'Gratuito'
                  }
                </p>
                {center.subscription.billingInterval && center.subscription.billingInterval !== 'none' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    /{center.subscription.billingInterval === 'yearly' ? 'anno' : 'mese'}
                  </p>
                )}
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {center.subscription.cancelAtPeriodEnd ? 'Termina il' : 'Rinnovo il'}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {center.subscription.currentPeriodEnd 
                    ? new Date(center.subscription.currentPeriodEnd).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short'
                      })
                    : '-'
                  }
                </p>
                {center.subscription.daysRemaining != null && center.subscription.status === 'trialing' && (
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {center.subscription.daysRemaining}g rimanenti
                  </p>
                )}
              </div>
            </div>
            
            {center.subscription.cancelAtPeriodEnd && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  L'abbonamento non verrà rinnovato e terminerà il{' '}
                  {new Date(center.subscription.currentPeriodEnd).toLocaleDateString('it-IT')}
                </span>
              </div>
            )}
            
            {center.subscription.status === 'past_due' && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>Pagamento in ritardo. Aggiorna il metodo di pagamento dalla sezione Fatturazione.</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <CreditCard className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              Piano: <span className="font-medium capitalize">{center.currentPlan || 'Trial'}</span>
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Gestisci gli abbonamenti dalla sezione Fatturazione
            </p>
          </div>
        )}
      </Card>
      
      {/* === SEZIONE TRASFERIMENTO === */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <ArrowRightLeft className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Trasferimento Centro
            </h2>
          </div>
          {!hasPendingTransfer && !migrationStatus && center.status === 'active' && (
            <button
              onClick={() => setShowTransferModal(true)}
              className="btn-secondary btn-sm"
            >
              <Send className="h-4 w-4 mr-2" />
              Trasferisci
            </button>
          )}
        </div>
        
        {/* Stato migrazione in corso */}
        {loadingMigration ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : migrationStatus ? (
          <MigrationStatusPanel 
            migration={migrationStatus} 
            onRefresh={() => { loadMigrationStatus(); loadOutgoingRequests(); }} 
          />
        ) : (
          <>
            {/* Richieste in uscita */}
            {outgoingRequests.length > 0 ? (
              <div className="space-y-3">
                {outgoingRequests.map((req) => (
                  <div
                    key={req.id}
                    className={`p-4 rounded-lg border ${
                      req.status === 'pending'
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : req.status === 'accepted'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Trasferimento a: {req.toOrganization}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Richiesto il {new Date(req.requestedAt).toLocaleDateString('it-IT')}
                          {req.daysRemaining && ` • Scade tra ${req.daysRemaining} giorni`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          req.status === 'pending'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                            : req.status === 'accepted'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                            : req.status === 'rejected'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {req.status === 'pending' ? 'In attesa' :
                           req.status === 'accepted' ? 'Accettata' :
                           req.status === 'rejected' ? 'Rifiutata' :
                           req.status === 'cancelled' ? 'Annullata' : 'Scaduta'}
                        </span>
                        {req.status === 'pending' && (
                          <button
                            onClick={() => handleCancelTransferRequest(req.id)}
                            className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                            title="Annulla richiesta"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-6">
                Nessuna richiesta di trasferimento attiva.
                <br />
                <span className="text-sm">
                  Per trasferire questo centro, richiedi un codice all'organizzazione ricevente.
                </span>
              </p>
            )}
          </>
        )}
      </Card>
      
      {/* Warning per stati speciali */}
      {center.status === 'suspended' && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                Centro Sospeso
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Questo centro è attualmente sospeso. Gli utenti non possono accedere.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {center.status === 'pending_deletion' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex items-center gap-3">
              <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  Cancellazione in Corso
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  Questo centro è programmato per la cancellazione.
                  {center.deletion?.scheduledAt && (
                    <> Data prevista: <strong>{new Date(center.deletion.scheduledAt).toLocaleDateString('it-IT')}</strong></>
                  )}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancelCenterDeletion}
              disabled={cancelingDeletion}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {cancelingDeletion ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Annulla cancellazione
            </button>
          </div>
        </div>
      )}

      {center.status === 'cancelled' && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
            <div>
              <p className="font-medium text-rose-800 dark:text-rose-200">
                Cancellato (workflow legacy)
              </p>
              <p className="text-sm text-rose-700 dark:text-rose-300">
                Centro nel vecchio flusso di cancellazione. Contatta il supporto se ritieni sia un errore.
                {center.scheduledDeletionDate && (
                  <> Data prevista: <strong>{new Date(center.scheduledDeletionDate).toLocaleDateString('it-IT')}</strong></>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {center.status === 'export_only' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Download className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <div>
              <p className="font-medium text-orange-800 dark:text-orange-200">
                Solo Esportazione Dati
              </p>
              <p className="text-sm text-orange-700 dark:text-orange-300">
                L'accesso operativo è terminato. È disponibile solo l'esportazione dei dati prima della cancellazione definitiva.
                {center.deletion?.scheduledAt && (
                  <> Data prevista: <strong>{new Date(center.deletion.scheduledAt).toLocaleDateString('it-IT')}</strong></>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* ============================================ */}
      {/* MODAL INVITO COORDINATORE */}
      {/* ============================================ */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleCloseInviteModal} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <UserPlus className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Invita Coordinatore
                  </h3>
                </div>
                <button
                  onClick={handleCloseInviteModal}
                  className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Se c'è un PIN da mostrare */}
              {inviteResult ? (
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        Invito Inviato!
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Email inviata a {inviteResult.email}
                    </p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        PIN di Verifica
                      </span>
                      <button
                        onClick={handleCopyPin}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <Copy className="h-4 w-4" />
                        {pinCopied ? 'Copiato!' : 'Copia'}
                      </button>
                    </div>
                    <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {inviteResult.pin}
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium mb-1">Importante!</p>
                        <p>
                          Comunica questo PIN a <strong>{inviteResult.nome} {inviteResult.cognome}</strong> di 
                          persona, per telefono o via chat. Senza il PIN non potrà completare la registrazione.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCloseInviteModal}
                    className="w-full btn-primary"
                  >
                    Ho comunicato il PIN
                  </button>
                </div>
              ) : (
                /* Form invito */
                <form onSubmit={handleInviteCoordinator} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nome *
                      </label>
                      <input
                        type="text"
                        value={inviteForm.nome}
                        onChange={(e) => setInviteForm({ ...inviteForm, nome: e.target.value })}
                        required
                        className="input"
                        placeholder="Mario"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cognome *
                      </label>
                      <input
                        type="text"
                        value={inviteForm.cognome}
                        onChange={(e) => setInviteForm({ ...inviteForm, cognome: e.target.value })}
                        required
                        className="input"
                        placeholder="Rossi"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      required
                      className="input"
                      placeholder="coordinatore@example.com"
                    />
                  </div>
                  
                  {/* Opzione coordinatore principale */}
                  {!mainCoordinatorId && (
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="setAsMain"
                        checked={inviteForm.setAsMain}
                        onChange={(e) => setInviteForm({ ...inviteForm, setAsMain: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                      />
                      <label htmlFor="setAsMain" className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium">Imposta come coordinatore principale</span>
                        <span className="text-gray-500 dark:text-gray-400 block text-xs">
                          Il coordinatore principale gestisce gli abbonamenti e le impostazioni
                        </span>
                      </label>
                    </div>
                  )}
                  
                  {/* Opzione PIN */}
                  <div className="flex items-start gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <input
                      type="checkbox"
                      id="requirePin"
                      checked={inviteForm.requirePin}
                      onChange={(e) => setInviteForm({ ...inviteForm, requirePin: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                    />
                    <label htmlFor="requirePin" className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="flex items-center gap-1">
                        <Shield className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Richiedi PIN di verifica</span>
                        <span className="text-green-600 text-xs">(consigliato)</span>
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 block text-xs mt-0.5">
                        Dovrai comunicare un PIN a 6 cifre al coordinatore per confermare la sua identità
                      </span>
                    </label>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseInviteModal}
                      className="btn-secondary"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={inviting}
                      className="btn-primary"
                    >
                      {inviting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Invia Invito
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ============================================ */}
      {/* MODAL TRASFERIMENTO */}
      {/* ============================================ */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => { setShowTransferModal(false); resetTransferForm(); }} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <ArrowRightLeft className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Trasferisci Centro
                  </h3>
                </div>
                <button
                  onClick={() => { setShowTransferModal(false); resetTransferForm(); }}
                  className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Istruzioni */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Come funziona:</strong> L'organizzazione che riceverà il centro deve 
                    generare un codice di trasferimento e comunicartelo. Inseriscilo qui sotto.
                  </p>
                </div>
                
                {/* Input codice */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Codice di Trasferimento *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={transferCode}
                      onChange={(e) => {
                        setTransferCode(e.target.value.toUpperCase())
                        setVerifiedOrg(null)
                      }}
                      placeholder="TRF-XXXX-2026-YYYY"
                      className="input flex-1 font-mono uppercase"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyTransferCode}
                      disabled={verifyingCode || !transferCode.trim()}
                      className="btn-secondary"
                    >
                      {verifyingCode ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Verifica'
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Org verificata */}
                {verifiedOrg && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        Codice Valido
                      </span>
                    </div>
                    <div className="text-sm text-green-700 dark:text-green-300">
                      <p><strong>Organizzazione:</strong> {verifiedOrg.name}</p>
                      {verifiedOrg.responsabile && (
                        <p><strong>Responsabile:</strong> {verifiedOrg.responsabile.nome} {verifiedOrg.responsabile.cognome}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Note */}
                {verifiedOrg && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Note (opzionale)
                    </label>
                    <textarea
                      value={transferNotes}
                      onChange={(e) => setTransferNotes(e.target.value)}
                      rows={2}
                      className="input"
                      placeholder="Aggiungi note per l'organizzazione ricevente..."
                    />
                  </div>
                )}
                
                {/* GDPR Reminder con minori */}
                {verifiedOrg && minoriCount > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium mb-2">
                          Attenzione: questo centro ha {minoriCount} minori attivi
                        </p>
                        <p className="mb-2">
                          Il trasferimento comporta il cambio del <strong>Titolare del trattamento dati</strong> ai sensi del GDPR. 
                          Come previsto dal contratto (DPA art. X):
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Tu sei responsabile di contattare i genitori/tutori</li>
                          <li>Tu devi raccogliere il consenso per il trasferimento</li>
                          <li>Tu devi documentare i consensi raccolti</li>
                          <li>I minori senza consenso saranno dimessi (dati non trasferiti)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Conferma GDPR */}
                {verifiedOrg && (
                  <div className="flex items-start gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <input
                      type="checkbox"
                      id="gdprConfirmed"
                      checked={gdprConfirmed}
                      onChange={(e) => setGdprConfirmed(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                    />
                    <label htmlFor="gdprConfirmed" className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Confermo di aver compreso le mie responsabilità</span>
                      <span className="text-gray-500 dark:text-gray-400 block text-xs mt-0.5">
                        {minoriCount > 0 
                          ? 'Mi impegno a raccogliere i consensi GDPR dai genitori/tutori prima del completamento del trasferimento.'
                          : 'Confermo di voler trasferire questo centro all\'organizzazione indicata.'
                        }
                      </span>
                    </label>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTransferModal(false); resetTransferForm(); }}
                    className="btn-secondary"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleSendTransferRequest}
                    disabled={sendingTransfer || !verifiedOrg || !gdprConfirmed}
                    className="btn-primary"
                  >
                    {sendingTransfer ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Invia Richiesta
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ============================================ */}
      {/* MODAL DETTAGLIO COORDINATORE (PIN) */}
      {/* ============================================ */}
      {showCoordinatorModal && selectedCoordinator && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleCloseCoordinatorModal} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Key className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Invito Coordinatore
                  </h3>
                </div>
                <button
                  onClick={handleCloseCoordinatorModal}
                  className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Loading */}
              {loadingCoordinatorInfo ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">Caricamento...</p>
                </div>
              ) : resendResult ? (
                /* Nuovo PIN dopo reinvio */
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        Nuovo Invito Inviato!
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Email inviata a {selectedCoordinator.email}
                    </p>
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium">Il PIN precedente non è più valido!</p>
                        <p className="mt-1">Comunica il NUOVO PIN al coordinatore.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        Nuovo PIN
                      </span>
                      <button
                        onClick={handleCopyCoordinatorPin}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <Copy className="h-4 w-4" />
                        {pinCopied ? 'Copiato!' : 'Copia'}
                      </button>
                    </div>
                    <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {resendResult.pin}
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCloseCoordinatorModal}
                    className="w-full btn-primary"
                  >
                    Ho comunicato il nuovo PIN
                  </button>
                </div>
              ) : coordinatorInviteInfo ? (
                <div className="space-y-4">
                  {/* Info coordinatore */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 dark:text-primary-400 font-medium text-lg">
                        {coordinatorInviteInfo.nome?.[0]}{coordinatorInviteInfo.cognome?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {coordinatorInviteInfo.nome} {coordinatorInviteInfo.cognome}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {coordinatorInviteInfo.email}
                      </p>
                    </div>
                  </div>
                  
                  {/* Stato invito */}
                  {coordinatorInviteInfo.isExpired ? (
                    /* Invito scaduto */
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">
                          Invito Scaduto
                        </span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        L'invito è scaduto il {new Date(coordinatorInviteInfo.inviteExpires).toLocaleDateString('it-IT')}. 
                        Reinvia l'invito per generare un nuovo link e PIN.
                      </p>
                    </div>
                  ) : coordinatorInviteInfo.pin ? (
                    /* PIN disponibile */
                    <>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <span className="font-medium text-blue-800 dark:text-blue-200">
                            In attesa di registrazione
                          </span>
                        </div>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Scade il {new Date(coordinatorInviteInfo.inviteExpires).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      
                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <Key className="h-4 w-4" />
                            PIN di Verifica
                          </span>
                          <button
                            onClick={handleCopyCoordinatorPin}
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                          >
                            <Copy className="h-4 w-4" />
                            {pinCopied ? 'Copiato!' : 'Copia'}
                          </button>
                        </div>
                        <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          {coordinatorInviteInfo.pin}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                        Comunica questo PIN a {coordinatorInviteInfo.nome} per completare la registrazione.
                      </p>
                    </>
                  ) : (
                    /* Invito senza PIN */
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="font-medium text-blue-800 dark:text-blue-200">
                          In attesa di registrazione
                        </span>
                      </div>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Questo invito non richiede PIN. Scade il {new Date(coordinatorInviteInfo.inviteExpires).toLocaleDateString('it-IT')}.
                      </p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleCloseCoordinatorModal}
                      className="flex-1 btn-secondary"
                    >
                      Chiudi
                    </button>
                    <button
                      onClick={handleResendInvite}
                      disabled={resendingInvite}
                      className="flex-1 btn-primary"
                    >
                      {resendingInvite ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      {coordinatorInviteInfo.isExpired ? 'Reinvia Invito' : 'Rigenera Invito'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
